<script setup lang="ts">
import { onMounted, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import {
    JahresertragSolarstromanlage,
    JahresertragSolarstromanlageCollection,
} from '@/models/JahresertragSolarstromanlage';
import KpJahrSelect from '@/components/KpJahrSelect.vue';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'jahr' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new JahresertragSolarstromanlageCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

onMounted(async () => {
    if (collection.loading.value !== true) {
        await collection.query();
    }
});

function editRecord(r: JahresertragSolarstromanlage) {
    router.push({ name: 'bg_jw_jahresertrag_solarstromanlage_edit', params: { id: r?.id } });
}

async function deleteRecord(r: JahresertragSolarstromanlage) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function newRecord() {
    router.push({ name: 'bg_jw_jahresertrag_solarstromanlage_edit', params: { id: 'new' } });
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:table-sort="tableSort"
            :title="$t('jahresertrag_solarstromanlagen')"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @editrecord="editRecord($event as JahresertragSolarstromanlage)"
            @deleterecord="deleteRecord($event as JahresertragSolarstromanlage)"
            @new="newRecord"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column field="jahr" :header="$t('jahr')" filter-field="jahr" :showFilterMenu="false">
                <template #body="slotProps">
                    <div>{{ slotProps.data.jahr }}</div>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <KpJahrSelect fluid v-model="filterModel.value" @change="filterCallback()"></KpJahrSelect>
                </template>
            </Column>
            <Column field="mittlerer_ertrag">
                <template #header>
                    <div class="super-header" v-html="$t('mittlerer_jahresertrag_kwh_pro_kwp')"></div>
                </template>
            </Column>
            <Column field="untere_grenze">
                <template #header> <div class="super-header" v-html="$t('untere_grenze_kwh_pro_kwp')"></div> </template
            ></Column>
            <Column field="obere_grenze">
                <template #header> <div class="super-header" v-html="$t('obere_grenze_kwh_pro_kwp')"></div> </template
            ></Column>
        </KpBaseCollectionGrid>
    </div>
</template>
<style scoped lang="scss">
.super-header {
    margin-bottom: -4px;
    font-weight: 700;
}
</style>
